import {
  AfterViewChecked, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Output,
  SimpleChanges, ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Chart } from 'chart.js';
import { Subscription } from 'rxjs';
import { FroelingData, FroelingSettingsSnapshot, KnxWeatherSnapshot, ThermometerReading, ZisterneHistoryPoint } from '../version.service';
import {
  AnalysisSummary, BOARD_LIMITS, BoardService, BoardState, SlotWindow, VALVES_ALL, WINDOW_PRESETS, WindowPreset,
  resolveWindow, sameWindow,
} from '../shared/board.service';
import { DeepLinkService, LinkTab, LinkWindow, formatLinkTime } from '../shared/deep-link.service';
import { uiLocale } from '../shared/chart.utils';
import { lockScroll, unlockScroll } from '../shared/scroll-lock';
import { leaveSheetEntry, pushSheetEntry } from '../popup-card/sheet-history';
import { BoardLive, BoardRef, CatalogGroup, Tr, boardTitle, buildCatalog, parseBoardId } from './board-catalog';
import {
  BoardData, BoardLiveHistory, BoardLoader, ExactStats, STAT_BUCKETS, bucketsFor, loadBoardData, loadExactStats, statSourcesOf,
} from './board-data';
import {
  BuildCtx, BuiltChart, BuiltSeries, StatPart, buildBoardChart, fmtNum, registerBoardBands, seriesStat, statLine, statSourcesOfSeries,
} from './board-charts';
import { AxisWidths, clearBoardTime, naturalAxes, registerBoardSync } from './board-sync';
import { boardPdf, PdfItem, PdfTable } from './board-pdf';
import { SettingsView, settingsLogNote, settingsPdf, settingsView } from './board-settings';
import {
  VALVE_SORTS, ValveEval, ValveEvalCells, ValveSort, loadValveEval, loadValves, nextValveSort, valveEvalCells, valveRoomsKey,
} from './board-valves';
import { VALVE_COLOR } from './valve-heatmap';
import { dataUrlBytes, printable } from './pdf-writer';

registerBoardSync();
registerBoardBands();

/** Time window presets: a rolling window (it follows "now"). */
const PRESETS: readonly { key: WindowPreset; label: string }[] = WINDOW_PRESETS.map(key => ({ key, label: 'BOARD_P_' + key.toUpperCase() }));
/** The narrowest window a drag / pinch may select. */
const MIN_WINDOW_MS = 10 * 60_000;
/** A rolling window (a preset) follows "now" this often. */
const LIVE_REFRESH_MS = 5 * 60_000;
/** Crosshair group of the on-screen charts. */
const SYNC_GROUP = 'board';
/** Link overlay key of the picker (phones: Back closes it, see sheet-history.ts). */
const PICKER_SHEET = 'overlay=boardPicker';
/** Offscreen chart size for the PDF (CSS px; rendered at 2× = 1600 × 680 px). */
const PDF_W = 800, PDF_H = 340;
/** From / to typed in: applied this long after the last change (or at once on Enter / leaving the field),
 *  not at every segment typed (a half-typed "19" year would load 1919). */
const RANGE_PAUSE_MS = 1500;
/** The earliest year a typed window may start in (the archive starts in 2025). */
const MIN_YEAR = 2000;

/** A series' stats below its chart: its parts (each with the time of an extreme as tooltip), "≈" when
 *  approximated; `print`: the line as the PDF prints it (times after the extremes). */
interface StatView { label: string; color: string; dash: number[]; parts: StatPart[]; approx: boolean; text: string; print: string; }

/** One chart of the board (view model). */
interface BoardView {
  id: string;
  ref: BoardRef | null;
  icon: string;
  title: string;
  data: BoardData | null;
  config: BuiltChart | null;
  loading: boolean;
  failed: boolean;
  stats: StatView[];
  /** /api/history/stats of the chart's sources over the window (null: not loaded yet). */
  exact: ExactStats | null;
  /** The fallback stats (a source without endpoint stats: an older backend) are loaded / being loaded. */
  statFallback: boolean;
  /** The fallback data at the stats' fixed resolution (STAT_BUCKETS): the same numbers on every screen and in the PDF. */
  statData: BoardData | null;
  /** Its series (null: not loaded yet). */
  statSeries: BuiltSeries[] | null;
  /** The valve series the data was loaded with (a room whose mapping arrives later is reloaded). */
  valveSeries: string | null;
  /** 'valves.all': the evaluation below the heatmap (null: not loaded yet) and its rows as shown. */
  valveEval: ValveEval | null;
  valveCells: { key: string; ev: ValveEval; rows: ValveEvalCells[] } | null;
  /** 'froeling.settings': its content, kept while snapshot, window, language and the hints' reading are the same. */
  settings: { key: string; fs: FroelingSettingsSnapshot; view: SettingsView; note: string | null } | null;
  seq: number;
}

/**
 * The Analyse board: the collected charts over one time window (BoardService: an admin's slot, or a
 * read-only snapshot link). The slot bar on top switches, creates, renames, duplicates, reorders and deletes
 * slots (admin). Title and note say what the problem is; presets (rolling), from / to, a drag (mouse) or
 * pinch (touch) in any chart set the window for all charts, ⟲ goes back to the previous one, "jetzt" moves
 * it to end now; every change reloads each chart for exactly that window at its own resolution (and its
 * stats over every raw point of it: true min / max with their times, as tooltips or, after a tap on the
 * stats, inline). Every edit of a slot is saved on the server. A snapshot shows the same without any of the
 * controls (the window as text, no zoom, legends not clickable). Hovering / tapping a time shows it in every
 * chart. 🔗 copies a snapshot link, 📄 exports a PDF with it (share sheet on phones, download on desktop).
 * Title and note grow with their text, so a phone shows all of it.
 */
@Component({
  selector: 'app-board',
  templateUrl: './board.component.html',
  styleUrls: ['./board.component.css', './board-valves.css', './board-settings.css'],
})
export class BoardComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @Input() thermometers: ThermometerReading[] = [];
  @Input() froeling: FroelingData | null = null;
  @Input() knxWeather: KnxWeatherSnapshot | null = null;
  /** The cistern's live 7-day history (where the archive has no level data). */
  @Input() zisterneHistory: ZisterneHistoryPoint[] = [];
  /** The Fröling's settings and their change log ('froelingSettings' frame): the settings widget (always live). */
  @Input() froelingSettings: FroelingSettingsSnapshot | null = null;
  @Input() darkMode = false;
  /** "← Dashboard": the tab to return to. */
  @Output() back = new EventEmitter<LinkTab>();
  /** The guest note's "Admin-Anmeldung". */
  @Output() login = new EventEmitter<void>();

  readonly presets = PRESETS;
  readonly limits = BOARD_LIMITS;
  views: BoardView[] = [];
  win: LinkWindow;
  /** The board shown (null: a note / loading instead). */
  state: BoardState | null = null;
  title = '';
  note = '';
  fromText = '';
  toText = '';
  /** Why the typed window was not applied (i18n key), or null. */
  rangeError: string | null = null;
  loadingCount = 0;
  phone = window.innerWidth < 576;
  wide = window.innerWidth >= 1400;

  pickerOpen = false;
  pickerFilter = '';
  catalog: CatalogGroup[] = [];

  pdfBusy = false;
  pdfFailed = false;
  /** Phones: the PDF is ready but the share sheet needs a fresh tap. */
  pdfReady: File | null = null;
  /** 🔗 just copied the snapshot link. */
  copied = false;
  /** The slot menu (⋯) is open; its delete asks first. */
  menuOpen = false;
  confirmDelete = false;

  private undo: SlotWindow[] = [];
  /** The state key / window the charts show (a new key: another board). */
  private shownKey = '';
  private shownWindow: SlotWindow | null = null;
  private shownVis = '';
  private copiedTimer: any = null;
  private loader = new BoardLoader();
  private loaderWin = '';
  private subs: Subscription[] = [];
  private liveTimer: any = null;
  private rangeTimer: any = null;
  private grownKey = '';
  @ViewChild('titleEl') private titleEl?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('noteEl') private noteEl?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('menuEl') private menuEl?: ElementRef<HTMLElement>;

  constructor(
    readonly board: BoardService,
    private readonly link: DeepLinkService,
    private readonly translate: TranslateService,
    private readonly zone: NgZone,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    this.win = resolveWindow({ preset: '24h' });
  }

  ngOnInit(): void {
    this.applyState();
    this.applyPickerLink();
    this.subs.push(
      this.board.update$.subscribe(() => this.applyState()),
      this.translate.onLangChange.subscribe(() => { this.refreshTitles(); this.rebuildAll(); this.refreshCatalog(); }),
      this.link.external$.subscribe(() => this.applyPickerLink()),
    );
    this.zone.runOutsideAngular(() => {
      this.liveTimer = setInterval(() => {
        if (!this.rolling || document.visibilityState !== 'visible' || this.editingRange()) return;
        this.zone.run(() => this.followNow());
      }, LIVE_REFRESH_MS);
    });
  }

  /** The window is a preset: it follows "now". */
  get rolling(): boolean { return !!this.state && 'preset' in this.state.window; }

  get readOnly(): boolean { return !this.state || this.state.readOnly; }

  /** A rolling window moved on since it was shown (≥ 1 min): show it ending now. */
  private followNow(): void {
    if (!this.state || !('preset' in this.state.window)) return;
    const next = resolveWindow(this.state.window);
    if (Math.abs(next.to - this.win.to) >= 60_000) this.setWin(next);
  }

  /**
   * The board from the service (on init and whenever it changes): another board (slot / snapshot) shows its
   * charts, window, title and note afresh; within one board only what changed (charts added / removed /
   * moved, the window, a legend click's stats).
   */
  private applyState(): void {
    const s = this.board.state;
    this.state = s;
    if (!s) {
      this.shownKey = '';
      this.shownWindow = null;
      this.syncViews([], false);
      if (this.pickerOpen) this.closePicker();
      return;
    }
    const fresh = s.key !== this.shownKey;
    if (fresh) {
      this.shownKey = s.key;
      this.revealActiveChip();
      this.undo = [];
      this.rangeError = null;
      this.statTimes = false;
      this.menuOpen = false;
      this.confirmDelete = false;
      if (s.readOnly && this.pickerOpen) this.closePicker();
    }
    // the text being typed is the state's already; another board's replaces it
    if (fresh || !this.focused(this.titleEl)) this.title = s.title;
    if (fresh || !this.focused(this.noteEl)) this.note = s.note;
    const vis = JSON.stringify(s.vis);
    const winChanged = fresh || !this.shownWindow || !sameWindow(s.window, this.shownWindow);
    if (winChanged) {
      this.shownWindow = s.window;
      this.shownVis = vis;
      this.syncViews(s.charts, false);
      this.setWin(resolveWindow(s.window));
      return;
    }
    this.syncViews(s.charts, true);
    // a legend click: the chart shows it already, its stats follow
    if (vis !== this.shownVis) { this.shownVis = vis; this.views.forEach(v => this.updateStats(v)); }
  }

  /** The active slot's chip into view (the strip scrolls sideways on phones), after it is rendered. */
  private revealActiveChip(): void {
    this.zone.runOutsideAngular(() => requestAnimationFrame(() => {
      this.host.nativeElement.querySelector('.bd-slot-on')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }));
  }

  private focused(el: ElementRef<HTMLElement> | undefined): boolean {
    return !!el && document.activeElement === el.nativeElement;
  }

  /** Title and note as high as their text (after every change of it, or of the width). */
  ngAfterViewChecked(): void {
    const key = `${this.title.length}|${this.note.length}|${window.innerWidth}`;
    if (key === this.grownKey) return;
    this.grownKey = key;
    // ngModel writes the text into the field a tick after this check: measure after that
    this.zone.runOutsideAngular(() => requestAnimationFrame(() => {
      this.grow(this.titleEl?.nativeElement);
      this.grow(this.noteEl?.nativeElement);
    }));
  }

  /** A textarea as high as its text. */
  grow(el: HTMLTextAreaElement | undefined): void {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + (el.offsetHeight - el.clientHeight) + 'px';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['darkMode'] && !changes['darkMode'].firstChange) this.rebuildAll();
    if (changes['thermometers'] || changes['froeling'] || changes['knxWeather']) {
      this.refreshTitles();
      if (this.pickerOpen) this.refreshCatalog();
      // a room's valve mapping arrived (or changed): reload its chart with the valve archive
      for (const v of this.views) {
        // the valve overview: a room or its valve mapping arrived / changed
        if (v.ref?.kind === 'valves' && valveRoomsKey(this.thermometers, v.ref.floor) !== v.valveSeries && !v.loading) this.load(v);
        if (v.ref?.kind !== 'thermo') continue;
        const series = this.room(v.ref.deviceId)?.valveSeries ?? null;
        if (series !== v.valveSeries && !v.loading) this.load(v);
      }
      // the weather chart's quantity / unit come with the station's frame
      if (changes['knxWeather'] && !changes['knxWeather'].previousValue) {
        for (const v of this.views) if (v.ref?.kind === 'wx' && v.data) this.build(v);
      }
    }
    // the cistern's live history arrived after its chart was (being) loaded (the archive may have nothing):
    // it reaches further back than before (a live update only adds a point and drops old ones)
    const zh = changes['zisterneHistory'];
    const since = (pts: ZisterneHistoryPoint[] | null | undefined) => pts?.length ? pts[0].ts : Infinity;
    if (zh && !zh.firstChange && since(zh.currentValue) < since(zh.previousValue)) {
      for (const v of this.views) if (v.ref?.kind === 'zisterne') this.load(v);
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    clearInterval(this.liveTimer);
    clearTimeout(this.rangeTimer);
    clearTimeout(this.copiedTimer);
    if (this.pickerOpen) unlockScroll();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.menuOpen) { this.closeMenu(); return; }
    if (this.pickerOpen) this.closePicker();
  }

  /** A click outside the slot menu closes it (not one whose target the click itself just removed, e.g. "Löschen"
   *  turning into its confirmation). */
  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const target = e.target as Node | null;
    if (this.menuOpen && target?.isConnected && !this.menuEl?.nativeElement.contains(target)) this.closeMenu();
  }

  @HostListener('window:resize')
  onResize(): void {
    const phone = window.innerWidth < 576;
    this.wide = window.innerWidth >= 1400;
    if (phone !== this.phone) { this.phone = phone; this.rebuildAll(); }
  }

  // ── board items ─────────────────────────────────────────────────────────────

  trackView = (_: number, v: BoardView) => v.id;
  trackGroup = (_: number, g: CatalogGroup) => g.key;
  trackSub = (i: number, sg: { title: string | null }) => sg.title ?? i;
  trackItem = (_: number, it: { id: string }) => it.id;

  private get live(): BoardLive {
    return { thermometers: this.thermometers, froeling: this.froeling, knxWeather: this.knxWeather };
  }

  private readonly t: Tr = (key, params) => this.translate.instant(key, params);

  private get lang(): string { return this.translate.currentLang ?? 'en'; }

  private room(deviceId: string): ThermometerReading | undefined {
    return this.thermometers.find(r => r.deviceId === deviceId);
  }

  /** The board's ids → views (kept by id, so a reorder keeps the charts; new ones load unless `loadNew` is off). */
  private syncViews(items: string[], loadNew: boolean): void {
    const old = new Map(this.views.map(v => [v.id, v]));
    this.views = items.map(id => old.get(id) ?? this.newView(id));
    if (!loadNew) return;
    for (const v of this.views) if (!old.has(v.id) && v.ref) this.load(v);
  }

  private newView(id: string): BoardView {
    const ref = parseBoardId(id);
    const head = ref ? boardTitle(ref, this.live, this.t, this.lang) : { icon: '❔', title: id };
    return {
      id, ref, ...head, data: null, config: null, loading: false, failed: false, stats: [], exact: null, statFallback: false,
      statData: null, statSeries: null, valveSeries: null, valveEval: null, valveCells: null, settings: null, seq: 0,
    };
  }

  private refreshTitles(): void {
    for (const v of this.views) {
      if (!v.ref) continue;
      const h = boardTitle(v.ref, this.live, this.t, this.lang);
      v.icon = h.icon;
      v.title = h.title;
    }
  }

  move(v: BoardView, delta: number): void { if (!this.readOnly) this.board.move(v.id, delta); }

  remove(v: BoardView): void { if (!this.readOnly) this.board.remove(v.id); }

  // ── slots ───────────────────────────────────────────────────────────────────

  trackSlot = (_: number, s: AnalysisSummary) => s.id;

  /** A chip's title: the slot's own, else "Neue Analyse"-like placeholder. */
  slotTitle(s: AnalysisSummary): string { return s.title.trim() || this.t('BOARD_SLOT_UNTITLED'); }

  openSlot(s: AnalysisSummary): void {
    this.closeMenu();
    if (s.id !== this.board.activeSlotId) this.board.openSlot(s.id);
  }

  newSlot(): void { this.closeMenu(); this.board.create(); }

  toggleMenu(e: Event): void {
    e.stopPropagation();
    this.menuOpen = !this.menuOpen;
    this.confirmDelete = false;
  }

  closeMenu(): void { this.menuOpen = false; this.confirmDelete = false; }

  /** "Umbenennen": the title is the slot's name — edited in place. */
  renameSlot(): void {
    this.closeMenu();
    const el = this.titleEl?.nativeElement;
    if (!el) return;
    el.focus();
    el.select();
  }

  duplicateSlot(): void {
    const id = this.board.activeSlotId;
    this.closeMenu();
    if (id) this.board.duplicate(id);
  }

  moveSlot(delta: number): void {
    const id = this.board.activeSlotId;
    if (id) this.board.moveSlot(id, delta);
  }

  /** The active slot's place in the list (for the menu's ← / →). */
  get slotIndex(): number { return this.board.slots.findIndex(s => s.id === this.board.activeSlotId); }

  deleteSlot(): void {
    if (!this.confirmDelete) { this.confirmDelete = true; return; }
    const id = this.board.activeSlotId;
    this.closeMenu();
    if (id) this.board.delete(id);
  }

  saveSnapshot(): void { this.board.saveSnapshot(); }

  // ── title / note ────────────────────────────────────────────────────────────

  /** The title is one line (a pasted line break becomes a space; Enter leaves the field). */
  onTitle(value: string): void {
    if (this.readOnly) return;
    const one = value.replace(/[\r\n]+/g, ' ');
    this.title = one;
    this.board.setTitle(one);
    if (one !== value && this.titleEl) this.titleEl.nativeElement.value = one;
  }

  onNote(value: string): void {
    if (this.readOnly) return;
    this.note = value;
    this.board.setNote(value);
  }

  // ── time window ─────────────────────────────────────────────────────────────

  get canUndo(): boolean { return this.undo.length > 0; }

  /** The preset the window is (rolling), else null. */
  get activePreset(): WindowPreset | null {
    const w = this.state?.window;
    return w && 'preset' in w ? w.preset : null;
  }

  preset(key: WindowPreset): void { this.choose({ preset: key }); }

  /** "jetzt": the same span, ending now (a rolling window just catches up with now). */
  toNow(): void {
    if (this.rolling) { this.followNow(); return; }
    const span = this.win.to - this.win.from;
    const now = Date.now();
    this.choose({ from: now - span, to: now });
  }

  stepBack(): void {
    if (!this.undo.length || this.readOnly) return;
    this.board.setWindow(this.undo.pop()!);   // update$ → applyState() shows it
  }

  /** From / to being typed (datetime-local, local time): applied after a pause, see {@link applyRange}. */
  onRangeEdit(): void {
    clearTimeout(this.rangeTimer);
    this.zone.runOutsideAngular(() => {
      this.rangeTimer = setTimeout(() => this.zone.run(() => this.applyRange()), RANGE_PAUSE_MS);
    });
  }

  /**
   * Apply the typed from / to (Enter, leaving the field, or a pause in typing). A start before 2000 (a
   * year still being typed), in the future or after the end is not applied; an end in the future is now.
   */
  applyRange(): void {
    clearTimeout(this.rangeTimer);
    this.rangeTimer = null;
    const from = parseInputTime(this.fromText);
    let to = parseInputTime(this.toText);
    if (from === this.win.from && to === this.win.to) { this.rangeError = null; return; }
    const now = Math.ceil(Date.now() / 60_000) * 60_000;
    let error: string | null = null;
    if (from == null || to == null) error = 'BOARD_RANGE_INCOMPLETE';
    else if (new Date(from).getFullYear() < MIN_YEAR || new Date(to).getFullYear() < MIN_YEAR) error = 'BOARD_RANGE_YEAR';
    else if (from >= now) error = 'BOARD_RANGE_FUTURE';
    else if (to <= from) error = 'BOARD_RANGE_INVALID';
    this.rangeError = error;
    if (error) return;
    to = Math.min(to!, now);
    this.choose({ from: from!, to });
    this.updateInputs();   // e.g. the end set back to now
  }

  /** A from / to field has the focus (the default window does not move under the cursor). */
  private editingRange(): boolean {
    const a = document.activeElement;
    return !!a && this.host.nativeElement.contains(a) && (a.id === 'bdFrom' || a.id === 'bdTo');
  }

  /** A drag / pinch in a chart (runs outside Angular; slots only). */
  private readonly onChartWindow = (w: LinkWindow): void => this.zone.run(() => {
    let { from, to } = w;
    if (to - from < MIN_WINDOW_MS) {
      const mid = (from + to) / 2;
      from = mid - MIN_WINDOW_MS / 2;
      to = mid + MIN_WINDOW_MS / 2;
    }
    if (Math.abs(from - this.win.from) < 30_000 && Math.abs(to - this.win.to) < 30_000) return;
    this.choose({ from, to });
  });

  /** A new window chosen by the user: the old one can be stepped back to. */
  private choose(w: SlotWindow): void {
    if (this.readOnly || !this.state) return;
    const before = this.state.window;
    this.rangeError = null;
    this.board.setWindow(w);   // update$ → applyState() shows it
    const after = this.board.state?.window;
    if (!after || sameWindow(before, after)) return;
    this.undo.push(before);
    if (this.undo.length > 50) this.undo.shift();
  }

  /** Show `w`: every chart's axis right away (with the data it has), then the data of exactly that window. */
  private setWin(w: LinkWindow): void {
    this.win = w;
    this.updateInputs();
    clearBoardTime(SYNC_GROUP);
    this.rebuildAll();
    for (const v of this.views) if (v.ref) this.load(v);
  }

  private updateInputs(): void {
    clearTimeout(this.rangeTimer);
    this.rangeTimer = null;
    this.fromText = formatLinkTime(this.win.from);
    this.toText = formatLinkTime(this.win.to);
  }

  /** "Mi., 21.10.2026, 06:00 – 14:00 (8 h)". */
  get windowLabel(): string {
    return windowText(this.win, this.t);
  }

  // ── data and charts ─────────────────────────────────────────────────────────

  /** Width of chart `v` (for the resolution of its data): half the grid in the wide grid, unless it spans both columns. */
  private chartWidth(v: BoardView): number {
    const grid = this.host.nativeElement.querySelector('.bd-grid') as HTMLElement | null;
    const w = grid?.clientWidth || this.host.nativeElement.clientWidth || window.innerWidth;
    const i = this.views.indexOf(v);
    return this.wide && this.views.length > 1 && !(i >= 0 && this.wideCard(i)) ? w / 2 : w;
  }

  /**
   * Card `i` spans both columns of the wide grid (≥ 1400 px, .bd-wide): the settings widget, and a chart that would
   * otherwise sit alone in its row beside an empty half — the last of an odd run of charts right before a settings
   * card, or the only chart of a board with settings. The order stays as on paper and in the reorder buttons.
   */
  wideCard(i: number): boolean {
    const vs = this.views;
    if (this.isSettings(vs[i])) return true;
    const charts = vs.filter(v => !this.isSettings(v)).length;
    if (charts === 1 && charts < vs.length) return true;
    if (i + 1 >= vs.length || !this.isSettings(vs[i + 1])) return false;
    let run = 0;
    for (let j = i; j >= 0 && !this.isSettings(vs[j]); j--) run++;
    return run % 2 === 1;
  }

  private loaderFor(win: LinkWindow): BoardLoader {
    const key = `${win.from}|${win.to}`;
    if (key !== this.loaderWin) { this.loader = new BoardLoader(); this.loaderWin = key; }
    return this.loader;
  }

  private get liveHistory(): BoardLiveHistory {
    return { zisterne: this.zisterneHistory, rooms: this.thermometers };
  }

  private load(v: BoardView): void {
    // the settings widget has no chart data (its content comes with the live frame, see settingsOf)
    if (!v.ref || v.ref.kind === 'froelingSettings') return;
    const seq = ++v.seq;
    const win = this.win;
    const ref = v.ref;
    const room = ref.kind === 'thermo' ? this.room(ref.deviceId) : undefined;
    const loader = this.loaderFor(win);
    v.valveSeries = ref.kind === 'valves' ? valveRoomsKey(this.thermometers, ref.floor) : room?.valveSeries ?? null;
    if (ref.kind === 'valves' && !ref.floor) this.loadValveEval(v, win, loader, seq);
    if (!v.loading) this.loadingCount++;
    v.loading = true;
    // the stats of the old window are gone; those of this one come from every raw point (the endpoint's
    // requests start along with the data; the chart's series then name all its sources)
    v.exact = null;
    v.statFallback = false;
    v.statData = null;
    v.statSeries = null;
    v.stats = [];
    loadExactStats(statSourcesOf(ref, room), win, loader).catch(() => { /* fetched again for the series */ });
    loadBoardData(ref, win, bucketsFor(this.chartWidth(v)), loader, room, this.liveHistory)
      .then(data => {
        if (seq !== v.seq) return;
        v.data = data;
        v.failed = false;
        this.build(v);
      })
      .catch(() => { if (seq === v.seq) v.failed = true; })
      .finally(() => {
        if (seq !== v.seq) return;
        v.loading = false;
        this.loadingCount = Math.max(0, this.loadingCount - 1);
      });
  }

  /**
   * The endpoint stats of every source of the chart's series (once per window; the requests are shared
   * through the loader). A source the backend has none for (an older one): the data at the fixed stats
   * resolution, as before.
   */
  private ensureStats(v: BoardView): void {
    if (!v.ref || !v.config) return;
    const sources = statSourcesOfSeries(v.config.series);
    const known = v.exact;
    if (known && sources.every(s => known.has(`${s.series}|${s.agg ?? ''}`))) return;
    const seq = v.seq, win = this.win, ref = v.ref;
    const loader = this.loaderFor(win);
    const room = ref.kind === 'thermo' ? this.room(ref.deviceId) : undefined;
    loadExactStats(sources, win, loader, new Map(known ?? [])).then(exact => {
      if (seq !== v.seq) return;
      v.exact = exact;
      this.updateStats(v);
      if (v.statFallback || ![...exact.values()].some(x => x === null)) return;
      v.statFallback = true;
      loadBoardData(ref, win, STAT_BUCKETS, loader, room, this.liveHistory)
        .then(data => {
          if (seq !== v.seq) return;
          v.statData = data;
          this.buildStats(v);
          this.updateStats(v);
        })
        .catch(() => { /* no stats */ });
    });
  }

  private ctxFor(v: BoardView): BuildCtx {
    const ro = this.readOnly;
    return {
      dark: this.darkMode, t: this.t, win: this.win, phone: this.phone, print: false,
      vis: this.toggles(v), group: SYNC_GROUP, live: this.live,
      // read-only: no drag / pinch window, legends not clickable (the crosshair and tooltips stay)
      onWindow: ro ? null : this.onChartWindow,
      onLegend: ro ? null : toggles => this.zone.run(() => this.board.setVis(v.id, toggles)),
    };
  }

  /** The chart's legend selection (series toggled away from their default: key → hidden). */
  private toggles(v: BoardView): Map<string, boolean> {
    const m = new Map<string, boolean>();
    const cv = this.state?.vis[v.id];
    for (const k of cv?.show ?? []) m.set(k, false);
    for (const k of cv?.hide ?? []) m.set(k, true);
    return m;
  }

  private build(v: BoardView): void {
    if (!v.ref || !v.data) return;
    v.config = buildBoardChart(v.ref, v.data, this.ctxFor(v));
    this.buildStats(v);
    this.updateStats(v);
    this.ensureStats(v);
  }

  /** The series the stats are computed from (the chart's own, at the stats' resolution). */
  private buildStats(v: BoardView): void {
    v.statSeries = v.ref && v.statData
      ? buildBoardChart(v.ref, v.statData, { ...this.ctxFor(v), onWindow: null, onLegend: null, group: null }).series : null;
  }

  private rebuildAll(): void {
    for (const v of this.views) this.build(v);
  }

  /** Which series a chart shows: the board's toggles over the series' defaults. */
  private visible(v: BoardView): boolean[] {
    const toggled = this.toggles(v);
    return (v.config?.series ?? []).map(s => !(toggled.has(s.key) ? toggled.get(s.key)! : s.hidden));
  }

  private updateStats(v: BoardView): void {
    v.stats = statViews(v.config?.series ?? [], this.visible(v), v.statSeries, v.exact, this.win, this.t);
  }

  /** The times of the extremes inline after them (a tap on the stats: phones have no tooltips). */
  statTimes = false;

  /** Some stats line has times to show. */
  get hasStatTimes(): boolean {
    return this.views.some(v => v.stats.some(s => s.parts.some(p => p.at)));
  }

  toggleStatTimes(): void {
    if (this.hasStatTimes || this.statTimes) this.statTimes = !this.statTimes;
  }

  // ── valve overview ──────────────────────────────────────────────────────────

  /** 'valves.all': the evaluation of the window (/api/history/stats) below the heatmap. */
  private loadValveEval(v: BoardView, win: LinkWindow, loader: BoardLoader, seq: number): void {
    v.valveEval = null;
    v.valveCells = null;
    // after the heatmap's data (the same requests, shared through the loader): the chart comes first
    const rooms = this.thermometers;
    loadValves(null, win, bucketsFor(this.chartWidth(v)), loader, rooms).catch(() => null)
      .then(() => seq === v.seq ? loadValveEval(win, loader, rooms) : null)
      .then(ev => { if (ev && seq === v.seq) v.valveEval = ev; })
      .catch(() => { if (seq === v.seq) v.valveEval = { rows: [], unavailable: true }; });
  }

  isValvesAll(v: BoardView): boolean {
    return v.ref?.kind === 'valves' && !v.ref.floor;
  }

  /** The evaluation's order (the slot's valvesSort / a snapshot's analyse.valves.all.sort; default the rooms' order). */
  valveSort(v: BoardView): ValveSort {
    const s = v.id === VALVES_ALL ? this.state?.valvesSort as ValveSort | null : null;
    return s && VALVE_SORTS.includes(s) ? s : 'floor';
  }

  /** A header click (slots only: a snapshot keeps its link's order). */
  sortValves(v: BoardView, col: 'floor' | 'mean' | 'open' | 'closed' | 'dt'): void {
    if (this.readOnly) return;
    const next = nextValveSort(this.valveSort(v), col);
    this.board.setValvesSort(next === 'floor' ? null : next);
  }

  /** "↓" / "↑" after the header of the column sorted by. */
  valveArrow(v: BoardView, col: string): string {
    const s = this.valveSort(v);
    return s === col ? ' ↑' : s === '-' + col ? ' ↓' : '';
  }

  /** aria-sort of a column header. */
  valveSortAria(v: BoardView, col: string): string | null {
    const s = this.valveSort(v);
    return s === col ? 'ascending' : s === '-' + col ? 'descending' : null;
  }

  /** The evaluation's rows as shown (kept per evaluation, order and language). */
  valveRows(v: BoardView): ValveEvalCells[] {
    if (!v.valveEval) return [];
    const sort = this.valveSort(v);
    const key = `${sort}|${this.lang}`;
    if (v.valveCells?.key !== key || v.valveCells.ev !== v.valveEval) {
      v.valveCells = { key, ev: v.valveEval, rows: valveEvalCells(v.valveEval, sort, this.lang) };
    }
    return v.valveCells.rows;
  }

  trackValve = (_: number, r: ValveEvalCells) => r.id;

  valveHasLikely(v: BoardView): boolean {
    return !!v.valveEval?.rows.some(r => r.likely);
  }

  /** The evaluation as the PDF prints it: a header and one line per room (in the table's order). */
  private async valvePdfTable(v: BoardView, win: LinkWindow, loader: BoardLoader): Promise<PdfTable | undefined> {
    const ev = v.valveEval ?? await loadValveEval(win, loader, this.thermometers).catch(() => null);
    if (!ev || !ev.rows.length) return undefined;
    const t = this.t;
    // no stats endpoint (an older backend): just say so
    if (ev.unavailable) return { title: t('BOARD_VALVES_EVAL'), head: [], rows: [], note: t('BOARD_VALVES_EVAL_UNAVAILABLE') };
    return {
      title: t('BOARD_VALVES_EVAL'),
      head: [t('BOARD_VALVES_COL_ROOM'), t('BOARD_VALVES_COL_MEAN'), t('BOARD_VALVES_COL_OPEN'), t('BOARD_VALVES_COL_CLOSED'),
        t('BOARD_VALVES_COL_DT')],
      rows: valveEvalCells(ev, this.valveSort(v), this.lang).map(r => ({
        cells: !r.hasValve ? [r.label, t('BOARD_VALVES_NONE'), '', '', r.dt]
          : r.failed ? [r.label, '–', '–', '–', r.dt] : [r.label, r.mean, r.open, r.closed, r.dt],
        bar: r.hasValve && r.meanPct != null ? r.meanPct / 100 : null,
        color: VALVE_COLOR,
      })),
      note: t('BOARD_VALVES_FOOT') + (ev.rows.some(r => r.likely) ? ' · ' + t('BOARD_VALVES_LIKELY_NOTE') : ''),
    };
  }

  // ── settings widget ─────────────────────────────────────────────────────────

  isSettings(v: BoardView): boolean {
    return v.ref?.kind === 'froelingSettings';
  }

  /** The settings widget's content for the window (null: no snapshot received yet); kept while nothing it shows changed. */
  settingsOf(v: BoardView): BoardView['settings'] {
    const fs = this.froelingSettings;
    if (!fs) return null;
    // A rolling window's end moves only every LIVE_REFRESH_MS (followNow), but a change arrives with the frame at once
    // (stamped with the server's time): on screen the log of a rolling window has no upper bound, so a change just
    // made is listed next to its new value. The PDF uses the frozen window it states.
    const logWin: LinkWindow = this.rolling ? { from: this.win.from, to: Infinity } : this.win;
    // the hints read the circulation return of the live reading (FS_HINT_CIRC_SENSOR)
    const f = this.froeling;
    const key = `${this.win.from}|${this.rolling ? 'live' : this.win.to}|${this.lang}|${!!(f?.circulationReturnNoSensor || f?.circulationReturnTempC === 0)}`;
    if (v.settings?.fs !== fs || v.settings.key !== key) {
      const view = settingsView(fs, f, logWin, this.t, uiLocaleOf(this.lang));
      v.settings = { key, fs, view, note: settingsLogNote(view, this.t) };
    }
    return v.settings;
  }

  trackSettingsGroup = (_: number, g: { id: string }) => g.id;

  // ── picker ──────────────────────────────────────────────────────────────────

  openPicker(): void {
    if (this.pickerOpen || this.readOnly) return;
    pushSheetEntry(PICKER_SHEET, this.link, this.zone);
    this.link.set('overlay', 'boardPicker');
    this.showPicker();
  }

  closePicker(): void {
    if (this.link.get('overlay') === 'boardPicker') this.link.set('overlay', null);
    this.hidePicker();
    leaveSheetEntry(PICKER_SHEET);
  }

  private applyPickerLink(): void {
    if (this.link.get('overlay') === 'boardPicker' && !this.readOnly) this.showPicker(); else this.hidePicker();
  }

  private showPicker(): void {
    if (this.pickerOpen) return;
    this.pickerOpen = true;
    this.pickerFilter = '';
    this.refreshCatalog();
    lockScroll();
  }

  private hidePicker(): void {
    if (!this.pickerOpen) return;
    this.pickerOpen = false;
    unlockScroll();
  }

  /** The active slot holds as many charts as the server takes: only removing is possible. */
  get pickerFull(): boolean { return (this.state?.charts.length ?? 0) >= BOARD_LIMITS.charts; }

  private refreshCatalog(): void {
    this.catalog = buildCatalog(this.live, this.t, this.lang);
  }

  /** The catalogue filtered by the search text (groups without a match are left out). */
  get filteredCatalog(): CatalogGroup[] {
    const q = this.pickerFilter.trim().toLowerCase();
    if (!q) return this.catalog;
    return this.catalog
      .map(g => ({
        ...g,
        subgroups: g.subgroups
          .map(sg => ({ ...sg, items: sg.items.filter(i => (i.title + ' ' + (sg.title ?? '') + ' ' + g.title).toLowerCase().includes(q)) }))
          .filter(sg => sg.items.length),
      }))
      .filter(g => g.subgroups.length);
  }

  groupEmpty(g: CatalogGroup): boolean {
    return !g.subgroups.some(sg => sg.items.length);
  }

  goBack(): void {
    this.back.emit(this.board.previousTab);
  }

  // ── 🔗 snapshot link ────────────────────────────────────────────────────────

  /** 🔗: the read-only snapshot link of what is on screen (a rolling window: ending now). */
  copyLink(): void {
    if (!this.state) return;
    this.followNow();
    const url = this.board.snapshotLink(this.win);
    const done = () => {
      this.copied = true;
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => { this.copied = false; }, 1500);
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done).catch(() => window.prompt('Link', url));
    else window.prompt('Link', url);
  }

  // ── PDF ─────────────────────────────────────────────────────────────────────

  async exportPdf(): Promise<void> {
    if (this.pdfBusy || !this.views.length || !this.state) return;
    this.pdfBusy = true;
    this.pdfFailed = false;
    this.pdfReady = null;
    try {
      // the window on screen (a rolling one ending now); the PDF's link names it as from / to
      this.followNow();
      const win = this.win;
      const loader = this.loaderFor(win);
      const ctx = (v: BoardView): BuildCtx => ({
        dark: false, t: this.t, win, phone: false, print: true,
        vis: this.toggles(v), onLegend: null, group: null, onWindow: null, live: this.live,
      });
      const items: ({ v: BoardView; built: BuiltChart; stats: StatView[] } | { v: BoardView; text: PdfItem })[] = [];
      for (const v of this.views) {
        if (!v.ref) continue;
        if (v.ref.kind === 'froelingSettings') {
          // no chart: the settings as text, the log over the PDF's window
          const view = this.froelingSettings
            ? settingsView(this.froelingSettings, this.froeling, win, this.t, uiLocaleOf(this.lang)) : null;
          items.push({ v, text: settingsPdf(v.title, view, this.readOnly, this.t) });
          continue;
        }
        const room = v.ref.kind === 'thermo' ? this.room(v.ref.deviceId) : undefined;
        const data = await loadBoardData(v.ref, win, bucketsFor(PDF_W * 2), loader, room, this.liveHistory).catch(() => v.data);
        const built = buildBoardChart(v.ref, data, ctx(v));
        // the stats as on screen: the endpoint's (the screen's requests, from the loader's cache), where it has
        // none the same fixed resolution
        const exact = await loadExactStats(statSourcesOfSeries(built.series), win, loader, new Map(v.exact ?? []));
        const missing = [...exact.values()].some(x => x === null);
        const statData = missing ? await loadBoardData(v.ref, win, STAT_BUCKETS, loader, room, this.liveHistory).catch(() => v.statData) : null;
        const statSeries = statData ? buildBoardChart(v.ref, statData, ctx(v)).series : null;
        const toggled = this.toggles(v);
        const vis = built.series.map(s => !(toggled.has(s.key) ? toggled.get(s.key)! : s.hidden));
        items.push({ v, built, stats: statViews(built.series, vis, statSeries, exact, win, this.t) });
      }
      // one y-axis width per side for all charts (the widest), so their plot areas line up on paper too
      const axes: AxisWidths = { left: 0, right: 0 };
      for (const it of items) {
        if (!('built' in it)) continue;
        const w = await measureAxes(it.built);
        axes.left = Math.max(axes.left, w.left);
        axes.right = Math.max(axes.right, w.right);
      }
      const charts: PdfItem[] = [];
      for (const it of items) {
        if (!('built' in it)) { charts.push(it.text); continue; }
        it.built.options.plugins.boardSync = { ...it.built.options.plugins.boardSync, axes };
        const stats = it.stats.map(s => ({ label: s.label, color: s.color, dash: s.dash, text: s.print }));
        charts.push({ title: it.v.title, jpeg: await renderJpeg(it.built), stats, empty: !it.built.points,
          ...(this.isValvesAll(it.v) ? { table: await this.valvePdfTable(it.v, win, loader) } : {}) });
      }
      const title = this.title.trim() || this.t('TAB_ANALYSE');
      const link = this.board.snapshotLink(win);
      const bytes = boardPdf({
        title,
        note: this.note,
        window: windowText(win, this.t),
        created: new Date().toLocaleString(uiLocale(), { dateStyle: 'medium', timeStyle: 'short' }),
        link,
        // printed without title and note (they are printed above): a link of a few lines, not of a page
        linkText: withoutKeys(link, ['title', 'note']),
        charts,
        labels: {
          window: this.t('BOARD_PDF_WINDOW'), created: this.t('BOARD_PDF_CREATED'), link: this.t('BOARD_PDF_LINK'),
          noData: this.t('BOARD_NO_DATA'), page: (n, total) => this.t('BOARD_PDF_PAGE', { n, total }),
        },
      });
      const file = new File([bytes], pdfFileName(this.title, win), { type: 'application/pdf' });
      await this.deliver(file);
    } catch (e) {
      console.error('PDF export failed', e);
      this.pdfFailed = true;
    } finally {
      this.pdfBusy = false;
    }
  }

  /** Phones: the share sheet (→ mail) when the browser can share files; otherwise (and on desktop) a download. */
  private async deliver(file: File): Promise<void> {
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
    if (this.phone && nav.share && nav.canShare?.({ files: [file] })) {
      try {
        await nav.share({ files: [file], title: file.name });
        return;
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        // the tap's activation expired while the PDF was being made: offer a button for a fresh one
        this.pdfReady = file;
        return;
      }
    }
    downloadFile(file);
  }

  async sharePdf(): Promise<void> {
    const file = this.pdfReady;
    if (!file) return;
    try {
      await navigator.share({ files: [file], title: file.name });
      this.pdfReady = null;
    } catch (e: any) {
      if (e?.name !== 'AbortError') { downloadFile(file); this.pdfReady = null; }
    }
  }

  downloadPdf(): void {
    if (this.pdfReady) downloadFile(this.pdfReady);
    this.pdfReady = null;
  }
}

/**
 * The stats line of a chart: for each visible series of the chart (its label, colour, dash) its summary —
 * from the endpoint's stats (`exact`, null: not loaded yet → none), where it has none from the same series
 * at the fallback resolution (`statSeries`), see seriesStat.
 */
function statViews(series: BuiltSeries[], visible: boolean[], statSeries: BuiltSeries[] | null, exact: ExactStats | null,
                   win: LinkWindow, t: Tr): StatView[] {
  if (!exact) return [];
  const byKey = new Map((statSeries ?? []).map(s => [s.key, s]));
  return series.flatMap((s, i) => {
    if (!visible[i]) return [];
    const r = seriesStat(s, statSeries ? byKey.get(s.key) : undefined, exact, win, t);
    return r ? [{ label: s.label, color: s.color, dash: s.dash, parts: r.parts, approx: r.approx, text: statLine(r), print: statLine(r, true) }] : [];
  });
}

/** Date / time locale of a UI language (the TranslateService's: on a language switch our onLangChange handler may
 *  run before AppComponent sets <html lang>, which uiLocale() reads). */
function uiLocaleOf(lang: string): string {
  return lang === 'de' ? 'de-DE' : 'en-GB';
}

/** A datetime-local value ("YYYY-MM-DDTHH:mm[:ss]", local time; also a year below 100) → epoch ms, else null. */
function parseInputTime(s: string): number | null {
  const m = /^(\d{4,6})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(s ?? '');
  if (!m) return null;
  const d = new Date(2000, 0, 1, +m[4], +m[5], +(m[6] ?? 0));
  d.setFullYear(+m[1], +m[2] - 1, +m[3]);   // new Date(y, …) would read a year below 100 as 19yy
  const t = d.getTime();
  return isNaN(t) ? null : t;
}

/** A URL with the given keys of its fragment's query left out ("…#analyse?c=…&note=…" → "…#analyse?c=…"). */
function withoutKeys(href: string, keys: string[]): string {
  const hash = href.indexOf('#');
  const q = hash >= 0 ? href.indexOf('?', hash) : -1;
  if (q < 0) return href;
  const kept = href.slice(q + 1).split('&').filter(p => p && !keys.includes(p.split('=')[0]));
  return href.slice(0, q) + (kept.length ? '?' + kept.join('&') : '');
}

/** "Mi., 21.10.2026, 06:00 – Mi., 21.10.2026, 14:00 (8 h)" (the end without its date on the same day). */
export function windowText(w: LinkWindow, t: Tr): string {
  const loc = uiLocale();
  const day = (ms: number) => new Date(ms).toLocaleDateString(loc, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = (ms: number) => new Date(ms).toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' });
  const sameDay = new Date(w.from).toDateString() === new Date(w.to).toDateString();
  const span = w.to - w.from;
  const hours = span / 3600_000;
  const num = (v: number) => { const r = Math.round(v * 10) / 10; return fmtNum(r, Number.isInteger(r) ? 0 : 1); };
  const spanText = hours < 48 ? t('BOARD_SPAN_H', { n: num(hours) }) : t('BOARD_SPAN_D', { n: num(hours / 24) });
  return `${day(w.from)} ${time(w.from)} – ${sameDay ? '' : day(w.to) + ' '}${time(w.to)} (${spanText})`;
}

/** "Analyse_Puffer_laedt_nicht.pdf" / "Analyse_2026-10-21.pdf". */
function pdfFileName(title: string, w: LinkWindow): string {
  const clean = printable(title).normalize('NFC').trim()
    .replace(/[^A-Za-z0-9ÄÖÜäöüß_-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 60);
  const d = new Date(w.to);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `Analyse_${clean || date}.pdf`;
}

function downloadFile(file: File): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** The y-axis widths a chart needs at the PDF's size (laid out offscreen, not drawn into the PDF). */
async function measureAxes(built: BuiltChart): Promise<AxisWidths> {
  const { chart, host } = offscreenChart(built);
  try {
    return naturalAxes(chart);
  } finally {
    chart.destroy();
    host.remove();
  }
}

/** A chart rendered offscreen (light theme, white background) as a JPEG of PDF_W × PDF_H at 2×. */
async function renderJpeg(built: BuiltChart): Promise<{ bytes: Uint8Array; w: number; h: number }> {
  const { chart, host, canvas } = offscreenChart(built);
  try {
    const out = document.createElement('canvas');
    out.width = canvas.width;
    out.height = canvas.height;
    const ctx = out.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0);
    return { bytes: dataUrlBytes(out.toDataURL('image/jpeg', 0.92)), w: out.width, h: out.height };
  } finally {
    chart.destroy();
    host.remove();
  }
}

/** A chart of PDF_W × PDF_H laid out and drawn on an offscreen canvas (the caller destroys it). */
function offscreenChart(built: BuiltChart): { chart: Chart; host: HTMLElement; canvas: HTMLCanvasElement } {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  const h = built.height ?? PDF_H;   // the valve heatmap: by its rows
  host.style.cssText = `position:fixed;left:-${PDF_W * 3}px;top:0;width:${PDF_W}px;height:${h}px;pointer-events:none;`;
  const canvas = document.createElement('canvas');
  canvas.width = PDF_W;
  canvas.height = h;
  canvas.style.width = PDF_W + 'px';
  canvas.style.height = h + 'px';
  host.appendChild(canvas);
  document.body.appendChild(host);
  try {
    const chart = new Chart(canvas, { type: built.type, data: built.data, options: built.options } as any);
    chart.update('none');
    return { chart, host, canvas };
  } catch (e) {
    host.remove();
    throw e;
  }
}
